import {
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { IRootState } from "redux/types";
import { UserType, auth } from "service";
import { formatDateToString } from "service/utils";
import { EAssignerType } from "types";
import { TimerCountDown } from "../TimerCountDown";
import { OrderStatusBadge } from "../order-status-badge";
import { ModalSendCoverLetter } from "./ModalReply";
import { addNewSpreadsheet } from "service/api";
import { CreateOrderLoadingModal } from "../create-order-loading-modal";

const TableCellWithTitle = ({ title, ...props }) => {
  return (
    <TableCell {...props} sx={{ p: "13px 12px", ...props.sx }}>
      <Typography>{title}</Typography>
    </TableCell>
  );
};

// const Badge = ({ title, ...props }) => {
//   return (
//     <Box
//       {...props}
//       sx={{
//         backgroundColor: "#F2F4F7",
//         p: "4px 12px 4px 10px",
//         display: "flex",
//         flexDirection: "row",
//         justifyContent: "center",
//         alignItems: "center",
//         height: "fit-content",
//         width: "fit-content",
//         borderRadius: "16px",
//         ...props.sx,
//       }}
//     >
//       <Typography>{title}</Typography>
//     </Box>
//   );
// };

const TextAutoLink = ({ text, link }: { text: string; link?: string }) => {
  if (!link) return <Typography>{text}</Typography>;
  return (
    <Link to={link}>
      <Typography>{text}</Typography>
    </Link>
  );
};

const tableBodyCellSx = {
  p: "22px 12px",
};

const GetEmpty = () => {
  const [loading, setLoading] = useState(false);
  const [isDefaultUser] = useState(auth.IsUserType(UserType.Default));

  return (
    <Box
      sx={{
        mt: "130px",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Typography
        fontSize={"16px"}
        lineHeight={"24px"}
        fontWeight={"600"}
        marginBottom="24px"
      >
        Заявок еще нет
      </Typography>
      {/*{auth.GetUser()?.uid == "62bcb1a9-546c-4b06-923f-69124e8a390f" ? (*/}
        {isDefaultUser ? 
        <Button
          variant="outlined"
          disabled={loading}
          onClick={async () => {
            try {
              setLoading(true);
              const rdata = await addNewSpreadsheet();
              window.location.replace(rdata.link);
            } catch (error) {
              alert(error);
            } finally {
              setLoading(false);
            }
          }}
        >
          Новая заявка
        </Button>
       : null}
      <CreateOrderLoadingModal isLoading={loading} />
    </Box>
  );
};

const assignerColumns = [
  {
    name: "Цифровой",
    key: EAssignerType.digital,
  },
  {
    name: "Финансовый",
    key: EAssignerType.finance,
  },
  {
    name: "Юридический",
    key: EAssignerType.legal,
  },
];

export const TableCellAssigner = ({
  el,
  column,
  valueByUserID,
}: {
  el: any;
  column: {
    name: string;
    key: EAssignerType;
  };
  valueByUserID: any;
}) => {
  const elem = el[column.key];
  const [open, setOpen] = useState(false);

  const isAdmin = auth.IsUserType(UserType.Admin);
  const isUser = auth.IsUserType(UserType.Default);

  if (!elem)
    return (
      <TableCell sx={{ ...tableBodyCellSx }} key={column.name}></TableCell>
    );

  const isReplyAvailable = () => isUser && elem?.status == "user_fixing"; // && elem?.reply_end_at && new Date(elem?.reply_end_at).getTime() > new Date().getTime();
  return (
    <TableCell
      sx={{
        ...tableBodyCellSx,
      }}
      key={column.name}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: "4px",
        }}
      >
        {valueByUserID && (
          <TextAutoLink
            text={valueByUserID[elem?.user_id]?.full_name}
            link={
              isAdmin ? `/admin/assignments?assigneeId=${elem?.user_id}` : ""
            }
          />
        )}
        <OrderStatusBadge status_name={elem?.status ?? ""} />
        <TimerCountDown
          endAtTimeStamp={new Date(elem?.reply_end_at).getTime() / 1000}
        />
        {isReplyAvailable() && (
          <Button
            size="small"
            variant="contained"
            sx={{
              padding: "4px 8px",
              fontSize: "14px",
            }}
            onClick={() => setOpen(true)}
          >
            Ответить
          </Button>
        )}
        <ModalSendCoverLetter
          open={open}
          handleClose={() => setOpen(false)}
          applicationId={el?.application_id}
          assignerType={column.key}
        />
      </Box>
    </TableCell>
  );
};

export const TableCellsAssigners = ({ el }: { el: any }) => {
  const {
    managers: { valueByUserID },
  } = useSelector((state: IRootState) => state.spreadsheets, shallowEqual);

  return (
    <>
      {assignerColumns.map((column) => {
        return (
          <TableCellAssigner
            column={column}
            el={el}
            valueByUserID={valueByUserID}
          />
        );
      })}
    </>
  );
};

export const RegistryTable = () => {
  const { spreadsheets } = useSelector(
    (state: IRootState) => state.spreadsheets,
    shallowEqual
  );
  const [isManager, setIsManager] = useState(auth.IsUserType(UserType.Manager));

  const getLoading = () => {
    return (
      <Box
        sx={{
          mt: "130px",
          mb: "130px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  };

  useEffect(() => {
    setIsManager(auth.IsUserType(UserType.Manager));
  }, [auth.GetUser()?.r]);

  const getTableBodyRow = () => {
    return spreadsheets.value?.applications.map((el, i) => {
      return (
        <TableRow key={`content-table-body-${i}`}>
          {isManager ? null : (
            <TableCell sx={{ ...tableBodyCellSx }}>
              <Typography>
                {formatDateToString(new Date(el.created_at))}
              </Typography>
            </TableCell>
          )}
          <TableCell>{el.no}</TableCell>
          {isManager ? (
            <>
              <TableCell sx={{ ...tableBodyCellSx }}>
                {el.signed_at != "0001-01-01T00:00:00Z"
                  ? formatDateToString(new Date(el.signed_at))
                  : null}
              </TableCell>

              <TableCell sx={{ ...tableBodyCellSx }}>
                {el.attrs?.from ? el.attrs?.from : null}
              </TableCell>
            </>
          ) : null}

          <TableCell sx={{ ...tableBodyCellSx }}>
            <OrderStatusBadge
              applicationId={el.id}
              status_name={el.status}
              withSwitch={isManager}
            />
          </TableCell>
          {isManager && (
            <>
              <TableCell sx={{ ...tableBodyCellSx }}>
                {el.attrs?.manufacturer ? el.attrs?.manufacturer : null}
              </TableCell>

              <TableCell sx={{ ...tableBodyCellSx }}>
                {el.attrs?.bin ? el.attrs?.bin : null}
              </TableCell>

              <TableCell sx={{ ...tableBodyCellSx }}>
                {el.attrs?.expenses_list ? el.attrs?.expenses_list : null}
              </TableCell>

              <TableCell sx={{ ...tableBodyCellSx }}>
                {el?.total_rows ? el?.total_rows : null}
              </TableCell>
            </>
          )}

          <TableCellsAssigners el={el} />
          <TableCell sx={{ ...tableBodyCellSx }}>
            <Link to={`${el.link}`} target={"_blank"}>
              <Typography fontWeight={"600"}>Открыть</Typography>
            </Link>
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <>
      {spreadsheets.loading ? (
        getLoading()
      ) : spreadsheets.value?.applications.length == 0 ? (
        <GetEmpty />
      ) : (
        <Box
          sx={{
            width: "100%",
            overflowX: "auto",
          }}
        >
          <Table>
            <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
              <TableRow>
                {isManager ? null : (
                  <TableCellWithTitle
                    title="Дата"
                    sx={{
                      width: "100%",
                    }}
                  />
                )}
                <TableCellWithTitle title="№" />
                {isManager && (
                  <>
                    <TableCellWithTitle title="Дата подписания заявки" />
                    <TableCellWithTitle title="От Кого" />
                  </>
                )}

                <TableCell sx={{ p: "13px 24px" }}>
                  <Typography>Статус</Typography>
                </TableCell>

                {isManager && (
                  <>
                    <TableCellWithTitle title="Производитель" />
                    <TableCellWithTitle title="БИН" />
                    <TableCellWithTitle title="Затраты заявленные Заявителем (по докум. заявки) без НДС и акцизы РК" />
                    <TableCellWithTitle title="Строки" />
                  </>
                )}

                {assignerColumns.map((column) => (
                  <TableCell sx={{ ...tableBodyCellSx }} key={column.name}>
                    {column.name}
                  </TableCell>
                ))}

                <TableCellWithTitle title="" />
              </TableRow>
            </TableHead>
            <TableBody>{getTableBodyRow()}</TableBody>
          </Table>
        </Box>
      )}
    </>
  );
};
